import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Grid,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Tooltip,
  Switch,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  alpha
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  VerifiedUser as VerifiedUserIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { UserGroup } from '../../types/userGroup';
import { Permission, PermissionSection, PermissionPage, PermissionAction } from '../../types/permission';
import { userGroupsApi } from '../../services/userGroupService';
import { permissionsApi } from '../../services/permissionService';

const PermissionManager: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  
  // حالات
  const [userGroup, setUserGroup] = useState<UserGroup | null>(null);
  const [permissions, setPermissions] = useState<Permission | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // حوار إعادة تعيين الصلاحيات
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  
  // تحميل البيانات
  useEffect(() => {
    const loadData = async () => {
      if (!groupId) {
        setError('معرف المجموعة غير صحيح');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // تحميل معلومات المجموعة
        const groupData = await userGroupsApi.getUserGroup(groupId);
        setUserGroup(groupData);
        
        // تحميل الصلاحيات
        const permissionsData = await permissionsApi.getGroupPermissions(groupId);
        setPermissions(permissionsData);
      } catch (err) {
        console.error('Error loading permission data:', err);
        setError('حدث خطأ أثناء تحميل البيانات');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [groupId]);
  
  // تحديث حالة صلاحية القسم كاملاً
  const handleSectionVisibilityChange = (sectionIndex: number, checked: boolean) => {
    if (!permissions) return;
    
    const newPermissions = {...permissions};
    newPermissions.sections = [...permissions.sections];
    newPermissions.sections[sectionIndex] = {
      ...newPermissions.sections[sectionIndex],
      visible: checked
    };
    
    // لا نقوم بتفعيل الصلاحيات تلقائياً - فقط نجعل القسم مرئي
    
    setPermissions(newPermissions);
  };

  // تفعيل جميع الصلاحيات في القسم
  const handleSelectAllSectionPermissions = (sectionIndex: number) => {
    if (!permissions) return;
    
    const newPermissions = {...permissions};
    newPermissions.sections = [...permissions.sections];
    newPermissions.sections[sectionIndex] = {
      ...newPermissions.sections[sectionIndex],
      visible: true
    };
    
    const pages = [...newPermissions.sections[sectionIndex].pages];
    newPermissions.sections[sectionIndex].pages = pages.map(page => ({
      ...page,
      visible: true,
      actions: page.actions.map(action => ({
        ...action,
        allowed: true
      }))
    }));
    
    setPermissions(newPermissions);
  };
  
  // تحديث حالة صلاحية الصفحة
  const handlePageVisibilityChange = (sectionIndex: number, pageIndex: number, checked: boolean) => {
    if (!permissions) return;
    
    const newPermissions = {...permissions};
    newPermissions.sections = [...permissions.sections];
    newPermissions.sections[sectionIndex] = {
      ...newPermissions.sections[sectionIndex],
      pages: [...newPermissions.sections[sectionIndex].pages]
    };
    
    newPermissions.sections[sectionIndex].pages[pageIndex] = {
      ...newPermissions.sections[sectionIndex].pages[pageIndex],
      visible: checked
    };
    
    // تحديث صلاحية مشاهدة الصفحة بشكل منفصل
    const viewPageActionIndex = newPermissions.sections[sectionIndex].pages[pageIndex].actions.findIndex(
      action => action.identifier === 'view_page'
    );
    
    if (viewPageActionIndex !== -1) {
      newPermissions.sections[sectionIndex].pages[pageIndex].actions[viewPageActionIndex] = {
        ...newPermissions.sections[sectionIndex].pages[pageIndex].actions[viewPageActionIndex],
        allowed: checked
      };
    }
    
    setPermissions(newPermissions);
  };
  
  // تحديث حالة صلاحية الإجراء
  const handleActionAllowedChange = (sectionIndex: number, pageIndex: number, actionIndex: number, checked: boolean) => {
    if (!permissions) return;
    
    const newPermissions = {...permissions};
    newPermissions.sections = [...permissions.sections];
    newPermissions.sections[sectionIndex] = {
      ...newPermissions.sections[sectionIndex],
      pages: [...newPermissions.sections[sectionIndex].pages]
    };
    
    newPermissions.sections[sectionIndex].pages[pageIndex] = {
      ...newPermissions.sections[sectionIndex].pages[pageIndex],
      actions: [...newPermissions.sections[sectionIndex].pages[pageIndex].actions]
    };
    
    newPermissions.sections[sectionIndex].pages[pageIndex].actions[actionIndex] = {
      ...newPermissions.sections[sectionIndex].pages[pageIndex].actions[actionIndex],
      allowed: checked
    };
    
    setPermissions(newPermissions);
  };
  
  // حفظ الصلاحيات
  const handleSave = async () => {
    if (!permissions || !groupId) return;
    
    try {
      setSaving(true);
      setError(null);
      
      const response = await permissionsApi.savePermissions(groupId, {
        sections: permissions.sections
      });
      
      setSuccess('تم حفظ الصلاحيات بنجاح');
      
      // تحديث الصلاحيات من الاستجابة
      setPermissions(response.permissions);
    } catch (err) {
      console.error('Error saving permissions:', err);
      setError('حدث خطأ أثناء حفظ الصلاحيات');
    } finally {
      setSaving(false);
    }
  };
  
  // إعادة تعيين جميع الصلاحيات
  const handleResetPermissions = async () => {
    if (!permissions || !groupId) return;
    
    try {
      setSaving(true);
      setError(null);
      
      // حذف الصلاحيات الحالية
      await permissionsApi.deletePermissions(groupId);
      
      // إعادة تحميل الصلاحيات (سيتم إنشاء صلاحيات افتراضية جديدة)
      const newPermissions = await permissionsApi.getGroupPermissions(groupId);
      setPermissions(newPermissions);
      
      setSuccess('تم إعادة تعيين الصلاحيات بنجاح');
      setResetDialogOpen(false);
    } catch (err) {
      console.error('Error resetting permissions:', err);
      setError('حدث خطأ أثناء إعادة تعيين الصلاحيات');
    } finally {
      setSaving(false);
    }
  };

  // تحديث الصلاحيات (إضافة الجديد بدون مسح الموجود)
  const handleUpdatePermissions = async () => {
    if (!permissions || !groupId) return;
    
    try {
      setSaving(true);
      setError(null);
      
      // استدعاء API الجديد للتحديث
      const result = await permissionsApi.updatePermissions(groupId);
      
      if (result.success) {
        setPermissions(result.permissions);
        setSuccess(result.message);
      } else {
        setError(result.message || 'حدث خطأ أثناء تحديث الصلاحيات');
      }
    } catch (err: any) {
      console.error('Error updating permissions:', err);
      setError(err.response?.data?.error || 'حدث خطأ أثناء تحديث الصلاحيات');
    } finally {
      setSaving(false);
    }
  };
  
  // رسم قسم الصلاحيات
  const renderPermissionSection = (section: PermissionSection, sectionIndex: number) => {
    return (
      <Accordion key={section.identifier} defaultExpanded sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ 
            bgcolor: theme.palette.mode === 'dark' 
              ? alpha(theme.palette.primary.main, 0.1)
              : alpha(theme.palette.primary.main, 0.05)
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {section.name}
              </Typography>
              
              <Tooltip title={section.visible ? 'القسم مرئي بالكامل' : 'القسم مخفي'}>
                <Box component="span" sx={{ display: 'inline-flex', ml: 1 }}>
                  {section.visible ? (
                    <VisibilityIcon fontSize="small" color="success" />
                  ) : (
                    <VisibilityOffIcon fontSize="small" color="disabled" />
                  )}
                </Box>
              </Tooltip>
            </Box>
            
            <Tooltip title="تفعيل جميع صلاحيات القسم">
              <IconButton
                color="primary"
                onClick={() => handleSelectAllSectionPermissions(sectionIndex)}
                size="small"
              >
                <CheckIcon />
              </IconButton>
            </Tooltip>
            <FormControlLabel
              control={
                <Switch
                  checked={section.visible}
                  onChange={(e, checked) => handleSectionVisibilityChange(sectionIndex, checked)}
                  color="primary"
                />
              }
              label={<Typography variant="body2">إظهار القسم</Typography>}
              sx={{ mr: 0 }}
              onClick={(e) => e.stopPropagation()}
            />
          </Box>
        </AccordionSummary>
        
        <AccordionDetails sx={{ p: 2 }}>
          <Grid container spacing={2}>
            {section.pages.map((page, pageIndex) => (
              <Grid item xs={12} md={6} lg={4} key={page.identifier}>
                {renderPermissionPage(page, sectionIndex, pageIndex)}
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  };
  
  // رسم صفحة الصلاحيات
  const renderPermissionPage = (page: PermissionPage, sectionIndex: number, pageIndex: number) => {
    return (
      <Card 
        variant="outlined" 
        sx={{ 
          borderRadius: 2, 
          height: '100%',
          borderColor: page.visible ? 'success.light' : 'divider',
          boxShadow: page.visible ? `0 0 0 1px ${theme.palette.success.light}` : 'none'
        }}
      >
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2">{page.name}</Typography>
              {page.visible && (
                <Tooltip title="الصفحة مرئية بالكامل">
                  <VisibilityIcon fontSize="small" color="success" />
                </Tooltip>
              )}
            </Box>
          }
          action={
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={page.visible}
                  onChange={(e, checked) => handlePageVisibilityChange(sectionIndex, pageIndex, checked)}
                  color="primary"
                />
              }
              label="تفعيل الوصول للصفحة"
            />
          }
          sx={{ 
            p: 1.5, 
            pb: 0.5,
            '& .MuiCardHeader-action': { m: 0 }
          }}
        />
        
        <CardContent sx={{ pt: 0.5, pb: '8px !important' }}>
          <Divider sx={{ mb: 1.5, mt: 0.5 }} />
          
          <Box sx={{ '& > *:not(:last-child)': { mb: 1 } }}>
            {page.actions.map((action, actionIndex) => (
              <FormControlLabel
                key={action.identifier}
                control={
                  <Checkbox
                    size="small"
                    checked={action.allowed}
                    onChange={(e, checked) => 
                      handleActionAllowedChange(sectionIndex, pageIndex, actionIndex, checked)
                    }
                    // لا يتم تعطيل الإجراءات حتى إذا كانت الصفحة مرئية
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2">{action.name}</Typography>
                    {action.allowed && !page.visible && (
                      <CheckIcon 
                        fontSize="small" 
                        color="success" 
                        sx={{ ml: 0.5, width: 16, height: 16 }}
                      />
                    )}
                  </Box>
                }
              />
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          borderRadius: 2,
          boxShadow: theme.shadows[2],
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* شريط زخرفي علوي */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 8,
            bgcolor: 'secondary.main',
          }}
        />
        
        {/* رأس الصفحة */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, mt: 1 }}>
          <Box>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SecurityIcon fontSize="large" color="secondary" />
              إدارة صلاحيات المجموعة
            </Typography>
            
            {userGroup && (
              <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.5 }}>
                المجموعة: <b>{userGroup.name}</b>
              </Typography>
            )}
          </Box>
          
          <IconButton 
            color="primary" 
            onClick={() => navigate('/admin/user-groups')}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Box>
        
        {/* رسائل النجاح والخطأ */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        
        {/* معلومات توضيحية */}
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }} icon={<InfoIcon />}>
          <Typography variant="body2">
            <b>إرشادات إدارة الصلاحيات:</b>
            <ul>
              <li>يمكنك تفعيل "إظهار القسم كاملاً" لمنح صلاحية كاملة لجميع الصفحات والإجراءات في هذا القسم.</li>
              <li>يمكنك تفعيل "تفعيل الوصول للصفحة" لمنح صلاحية مشاهدة الصفحة (لن يؤثر على باقي الصلاحيات).</li>
              <li>يمكنك تحديد إجراءات محددة بشكل منفصل للسماح بها في كل صفحة.</li>
              <li>الإجراءات مستقلة عن بعضها - يمكنك منح صلاحية مشاهدة صفحة العملاء دون منح صلاحية إضافة/تعديل/حذف.</li>
              <li><b>تحديث الصلاحيات:</b> يضيف الصلاحيات الجديدة مع الحفاظ على الإعدادات الحالية.</li>
              <li><b>إعادة تعيين:</b> يمسح جميع الصلاحيات ويعيدها للحالة الافتراضية (كلها مغلقة).</li>
            </ul>
          </Typography>
        </Alert>
        
        {/* تحميل البيانات */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              جاري تحميل البيانات...
            </Typography>
          </Box>
        ) : permissions ? (
          <>
            {/* أقسام الصلاحيات */}
            <Box sx={{ mb: 4 }}>
              {permissions.sections.map((section, index) => 
                renderPermissionSection(section, index)
              )}
            </Box>
            
            <Divider sx={{ my: 3 }} />
            
            {/* أزرار الإجراءات */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={saving}
                onClick={handleSave}
                sx={{ borderRadius: 2, px: 4, py: 1.2 }}
              >
                {saving ? 'جاري الحفظ...' : 'حفظ الصلاحيات'}
              </Button>
              
              <Button
                variant="outlined"
                color="info"
                startIcon={<RefreshIcon />}
                disabled={saving}
                onClick={handleUpdatePermissions}
                sx={{ borderRadius: 2, px: 3, py: 1.2 }}
              >
                تحديث الصلاحيات
              </Button>
              
              <Button
                variant="outlined"
                color="error"
                startIcon={<RefreshIcon />}
                disabled={saving}
                onClick={() => setResetDialogOpen(true)}
                sx={{ borderRadius: 2, px: 3, py: 1.2 }}
              >
                إعادة تعيين
              </Button>
              
              <Button
                variant="outlined"
                color="primary"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/admin/user-groups')}
                disabled={saving}
                sx={{ borderRadius: 2, px: 3, py: 1.2 }}
              >
                إلغاء
              </Button>
            </Box>
          </>
        ) : (
          <Alert severity="warning">
            لم يتم العثور على بيانات الصلاحيات
          </Alert>
        )}
      </Paper>
      
      {/* حوار تأكيد إعادة التعيين */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => !saving && setResetDialogOpen(false)}
      >
        <DialogTitle>تأكيد إعادة تعيين الصلاحيات</DialogTitle>
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد من رغبتك في إعادة تعيين جميع صلاحيات مجموعة "{userGroup?.name}"؟
            <br />
            سيتم إلغاء جميع الصلاحيات الحالية والعودة إلى الإعدادات الافتراضية.
            <br />
            هذا الإجراء لا يمكن التراجع عنه.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setResetDialogOpen(false)} 
            color="primary"
            disabled={saving}
          >
            إلغاء
          </Button>
          <Button 
            onClick={handleResetPermissions} 
            color="error" 
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
          >
            {saving ? 'جاري إعادة التعيين...' : 'تأكيد إعادة التعيين'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PermissionManager;